import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import HelpCenterHero from "@/components/help/HelpCenterHero";
import HelpCategoryGrid from "@/components/help/HelpCategoryGrid";
import PopularArticlesWidget from "@/components/help/PopularArticlesWidget";
import GettingStartedSection from "@/components/help/GettingStartedSection";
import ContactSupportCard from "@/components/help/ContactSupportCard";
import TourSelectionCard from "@/components/help/TourSelectionCard";
import { HelpArticle } from "@/types/help";

const SellerHelpHomePage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('help_articles' as any)
      .select('*')
      .eq('user_type', 'seller')
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching articles:', error);
    } else {
      setArticles((data || []) as unknown as HelpArticle[]);
    }
    setLoading(false);
  };

  const categories = useMemo(() => {
    const categoryMap = new Map<string, number>();
    articles.forEach((article) => {
      categoryMap.set(article.category, (categoryMap.get(article.category) || 0) + 1);
    });
    return Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }));
  }, [articles]);

  const handleCategoryClick = (category: string) => {
    navigate('/seller/help/articles', { state: { selectedCategory: category } });
  };

  const handleArticleClick = (articleId: string) => {
    navigate('/seller/help/articles', { state: { expandedArticleId: articleId } });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      navigate('/seller/help/articles', { state: { searchQuery: query } });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="py-8">
        <HelpCenterHero
          title="Seller Help Center"
          subtitle="Find answers to common questions and learn how to make the most of GoDyspo"
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            <GettingStartedSection 
              userType="seller" 
              onArticleClick={handleArticleClick}
            />
          </div>
          <div className="space-y-6">
            <TourSelectionCard userType="seller" />
            <PopularArticlesWidget 
              articles={articles}
              onArticleClick={handleArticleClick}
            />
            <ContactSupportCard />
          </div>
        </div>

        <HelpCategoryGrid
          categories={categories}
          onSelectCategory={handleCategoryClick}
        />
      </div>
    </div>
  );
};

export default SellerHelpHomePage;
